/**
 * ThreatZoneLayer - Red polygons for high-pollution areas
 */

import { Source, Layer, type FillLayer, type LineLayer } from 'react-map-gl';
import type { ThreatZoneGeoJSON } from '@/types';

interface ThreatZoneLayerProps {
  zone: ThreatZoneGeoJSON;
  index?: number;
}

const THREAT_COLORS = {
  low: {
    fill: 'rgba(255, 221, 0, 0.1)',
    border: 'rgba(255, 221, 0, 0.5)',
  },
  medium: {
    fill: 'rgba(255, 149, 0, 0.15)',
    border: 'rgba(255, 149, 0, 0.6)',
  },
  high: {
    fill: 'rgba(255, 51, 102, 0.2)',
    border: 'rgba(255, 51, 102, 0.7)',
  },
  critical: {
    fill: 'rgba(153, 0, 102, 0.25)',
    border: 'rgba(153, 0, 102, 0.8)',
  },
};

export function ThreatZoneLayer({ zone, index = 0 }: ThreatZoneLayerProps) {
  const colors = THREAT_COLORS[zone.properties.threatLevel];
  const sourceId = `threat-zone-${index}`;

  // Fill layer
  const fillLayer: FillLayer = {
    id: `${sourceId}-fill`,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': colors.fill,
      'fill-opacity': 0.8,
    },
  };

  // Border layer with animation hint
  const borderLayer: LineLayer = {
    id: `${sourceId}-border`,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': colors.border,
      'line-width': 2,
      'line-dasharray': [2, 2],
    },
  };

  // Outer glow effect
  const glowLayer: LineLayer = {
    id: `${sourceId}-glow`,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': colors.border,
      'line-width': 6,
      'line-blur': 4,
      'line-opacity': 0.5,
    },
  };

  return (
    <Source id={sourceId} type="geojson" data={zone}>
      <Layer {...fillLayer} />
      <Layer {...glowLayer} />
      <Layer {...borderLayer} />
    </Source>
  );
}

export default ThreatZoneLayer;
